/**
 * The ring and the callout for a guided creation flow.
 *
 * 🔴 THERE IS NO OVERLAY, AND THAT IS DELIBERATE. The user is filling in a real
 * form: they need to click other fields, scroll the dialog, open a select whose
 * list is portalled elsewhere, and type with arrow keys. Everything this renders
 * is `pointer-events: none` except the callout's own buttons, so the page
 * underneath behaves exactly as it does with no flow running.
 */

import React from 'react';
import { createPortal } from 'react-dom';

import { useTourGuide } from '../TourGuideProvider';
import { modalSafeChromeProps } from './modalSafe';
import type { CalloutSide } from '../tasks/types';
import type { TaskTargetRect } from '../engine/TaskEngine';

/** Gap between the ring and the callout. */
const GAP = 14;
/** Kept in sync with `.tour-task-callout { width }`. */
const CALLOUT_WIDTH = 320;
/**
 * Fallback height, used only for the very first paint of a step.
 *
 * 🔴 A CONSTANT IS NOT GOOD ENOUGH HERE, which is why the real height is measured
 * below. These descriptions run to several sentences and their rendered height
 * varies by well over a hundred pixels, so a fixed guess made the top/bottom
 * placements and the bottom clamp wrong by that much — the callout drifted away
 * from the control it was describing, which is half of "the guide is somewhere and
 * the field is somewhere".
 */
const CALLOUT_FALLBACK_HEIGHT = 220;
/** Never let the callout touch the viewport edge. */
const MARGIN = 12;
/** Breathing room around the spotlight's hole — the dialog or the bare target. */
const SPOTLIGHT_PAD = 8;
/** Room reserved for the progress bar along the bottom. */
const BOTTOM_BAR = 78;

interface Placement {
  top: number;
  left: number;
  side: CalloutSide;
}

/**
 * Where to put the callout.
 *
 * Tries the authored side, FLIPS to its opposite when there is not enough room,
 * and clamps along the other axis. Flipping rather than shrinking because a
 * callout that overhangs the viewport loses its buttons — and the buttons are
 * the only way forward on a manual step.
 */
export function placeCallout(
  target: TaskTargetRect,
  side: CalloutSide,
  viewport: { width: number; height: number },
  /** The callout's real rendered height; the fallback is only for first paint. */
  height: number = CALLOUT_FALLBACK_HEIGHT,
  /** Bounds the card must stay out of — the dialog holding the target. */
  avoid: TaskTargetRect | null = null,
): Placement {
  /**
   * 🔴 STEP OUTSIDE THE DIALOG FIRST, when there is one.
   *
   * Placing the card next to the FIELD puts it on top of the field's neighbours:
   * these dialogs are 700–900px wide, so "14px to the right of this input" is
   * almost always squarely over another input. That is what made the guide cards
   * disturb the form.
   *
   * So when the target is inside a dialog the card goes beside the DIALOG —
   * vertically aligned with the target so the connection is still obvious — and
   * only falls back to field-relative placement when neither margin can hold it
   * (a narrow window, where covering something is unavoidable and the clamps
   * below at least keep the card on screen and its buttons reachable).
   */
  if (avoid) {
    const roomRight = viewport.width - (avoid.left + avoid.width) - GAP - MARGIN;
    const roomLeft = avoid.left - GAP - MARGIN;
    const gutter = Math.max(roomRight, roomLeft);

    if (gutter >= CALLOUT_WIDTH) {
      const left =
        roomRight >= CALLOUT_WIDTH
          ? avoid.left + avoid.width + GAP
          : avoid.left - GAP - CALLOUT_WIDTH;
      const top = Math.max(
        MARGIN,
        Math.min(
          target.top + target.height / 2 - height / 2,
          Math.max(MARGIN, viewport.height - BOTTOM_BAR - height),
        ),
      );
      return { side: roomRight >= CALLOUT_WIDTH ? 'right' : 'left', left, top };
    }
  }

  const fits = {
    right: target.left + target.width + GAP + CALLOUT_WIDTH <= viewport.width - MARGIN,
    left: target.left - GAP - CALLOUT_WIDTH >= MARGIN,
    bottom: target.top + target.height + GAP + height <= viewport.height - BOTTOM_BAR,
    top: target.top - GAP - height >= MARGIN,
  };

  const opposite: Record<CalloutSide, CalloutSide> = {
    right: 'left',
    left: 'right',
    top: 'bottom',
    bottom: 'top',
  };

  // The authored side, else its opposite, else whichever side does fit, else the
  // authored one anyway — clamping below keeps even that on screen.
  const chosen: CalloutSide = fits[side]
    ? side
    : fits[opposite[side]]
      ? opposite[side]
      : (['right', 'bottom', 'left', 'top'] as CalloutSide[]).find((s) => fits[s]) ?? side;

  let top: number;
  let left: number;

  if (chosen === 'right' || chosen === 'left') {
    left = chosen === 'right' ? target.left + target.width + GAP : target.left - GAP - CALLOUT_WIDTH;
    top = target.top;
  } else {
    left = target.left + target.width / 2 - CALLOUT_WIDTH / 2;
    top = chosen === 'bottom' ? target.top + target.height + GAP : target.top - GAP - height;
  }

  const clamp = (value: number, min: number, max: number) =>
    Math.max(min, Math.min(value, Math.max(min, max)));

  return {
    side: chosen,
    left: clamp(left, MARGIN, viewport.width - CALLOUT_WIDTH - MARGIN),
    top: clamp(top, MARGIN, viewport.height - BOTTOM_BAR - height),
  };
}

export function TaskSpotlight() {
  const tour = useTourGuide();
  const calloutRef = React.useRef<HTMLElement | null>(null);
  const [calloutHeight, setCalloutHeight] = React.useState(CALLOUT_FALLBACK_HEIGHT);
  const [viewport, setViewport] = React.useState(() => ({
    width: typeof window === 'undefined' ? 0 : window.innerWidth,
    height: typeof window === 'undefined' ? 0 : window.innerHeight,
  }));

  React.useEffect(() => {
    const onResize = () => setViewport({ width: window.innerWidth, height: window.innerHeight });
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const task = tour?.task;
  const stepId = task?.step?.id ?? null;
  const description = task?.step?.description ?? '';

  /**
   * Measure the callout after it renders, and re-place from the real number.
   *
   * `useLayoutEffect` so the correction happens before the browser paints — in an
   * effect the user would see the callout jump. Keyed on the step AND its copy
   * because the height changes with the text, not with the step index alone.
   */
  React.useLayoutEffect(() => {
    const el = calloutRef.current;
    if (!el) return;
    const measured = el.offsetHeight;
    if (measured > 0 && measured !== calloutHeight) setCalloutHeight(measured);
  }, [stepId, description, calloutHeight]);

  if (!task?.running || !task.step) return null;

  /*
    🔴 NOTHING ON SCREEN UNTIL THE STEP'S OWN SCREEN IS. `awaiting` is set while
    the engine is still setting the step up — the route has changed, the page (or
    a `lazy()` chunk behind it) has not finished rendering, and the control this
    card describes does not exist yet. Published state arrives the instant a step
    is entered, so without this the card appeared over a half-loaded page, or
    over the PREVIOUS screen entirely: the reported "the first guide is already
    showing before the tour has started on that page".

    The bar stays up throughout — the press is still acknowledged, and the flow
    still says which step it is on — and the engine's own five-second give-up
    (`lost`) clears `awaiting`, so a step whose target never comes still gets its
    card, with the "this control is not on screen" line and a working Next.
    See `TaskRunState.step.awaiting`.
  */
  if (task.step.awaiting) return null;

  const { step, target } = task;
  const placement = target
    ? placeCallout(target, step.side, viewport, calloutHeight, task.avoid)
    : null;

  return createPortal(
    <>
      {/*
        Dims everything except the target — including the REST OF THE DIALOG
        when the target lives in one, not just the page behind it. That is
        deliberate: the point of a form step's spotlight is to pull the eye to
        the one field being explained, the same way the product tour narrows
        attention to a single element rather than lighting up its whole
        container. `pointer-events: none` throughout, same as the ring: this
        is framing, not a barrier, so every field — dimmed or not — stays
        exactly as clickable, scrollable and typeable as it is with no flow
        running.
      */}
      {step.spotlight && target && (
        <div
          className="tour-task-overlay"
          data-testid="tour-task-overlay"
          aria-hidden="true"
          style={{
            top: target.top - SPOTLIGHT_PAD,
            left: target.left - SPOTLIGHT_PAD,
            width: target.width + SPOTLIGHT_PAD * 2,
            height: target.height + SPOTLIGHT_PAD * 2,
          }}
        />
      )}

      {/*
        The ring. `pointer-events: none` is what keeps the highlighted control
        clickable — the ring sits ON TOP of it, so without that the user would be
        clicking the decoration instead of the field.
      */}
      {target && (
        <div
          className="tour-task-ring"
          data-testid="tour-task-ring"
          aria-hidden="true"
          style={{
            top: target.top,
            left: target.left,
            width: target.width,
            height: target.height,
          }}
        />
      )}

      <aside
        ref={calloutRef}
        className="tour-task-callout"
        data-testid="tour-task-callout"
        data-side={placement?.side ?? 'center'}
        data-anchored={target ? 'true' : 'false'}
        // Keeps a click on Next from reading as an outside-click and closing the
        // dialog the flow is filling in. See `modalSafeChromeProps`.
        {...modalSafeChromeProps}
        // Announced politely: it is guidance, not an interruption. The step's own
        // heading changes per step, which is what a screen reader reads out.
        role="status"
        aria-live="polite"
        style={
          placement
            ? { top: placement.top, left: placement.left }
            : // Nothing to point at (yet). Centred rather than hidden — the copy
              // still tells the user what to do, and Skip is still reachable.
              { bottom: BOTTOM_BAR + 12, left: '50%', transform: 'translateX(-50%)' }
        }
      >
        <p className="tour-task-callout__step">
          Step {Math.min(task.index + 1, task.total)} of {task.total}
        </p>
        <h2 className="tour-task-callout__title">{step.title}</h2>
        <p className="tour-task-callout__body">{step.description}</p>

        {/*
          Names the control that ACTUALLY gets past this step, which differs by
          step shape — this line used to say "Skip it to carry on" while Skip
          meant skipping one step. It means skipping the whole module now, so
          pointing a stuck user at it would throw away the rest of a walkthrough
          that is otherwise fine.
        */}
        {step.lost && (
          <p className="tour-task-callout__warn">
            {step.auto
              ? 'This control is not on screen. Use Skip step to carry on.'
              : 'This control is not on screen. Press Next to carry on.'}
          </p>
        )}

        <div className="tour-task-callout__actions">
          {/*
            🔴 NEVER DISABLED, and that is the fix rather than a preference. It
            used to be `disabled={task.index <= 0}`, so on the first step of a
            flow — the moment a visitor is most likely to realise they opened the
            wrong thing — the one control they reach for was inert. Back now
            always does something: an earlier step of the flow when there is one,
            otherwise out of the flow and back a stop in the tour. See
            `TourGuideApi.taskPrevious`.
          */}
          <button
            type="button"
            className="tour-task-callout__btn"
            onClick={() => tour?.taskPrevious()}
            data-testid="tour-task-back"
          >
            Back
          </button>

          {/*
            🔴 SKIP MEANS "SKIP THIS MODULE", NOT "SKIP THIS STEP", and it says
            so. As a per-step control it was the only way past a flow whose
            primary button reads "Try it: build a campaign" — so declining the
            invitation cost one press per step of the build, and then more
            presses through the rest of the module's submodules. Nobody skips one
            step of a walkthrough they have decided not to follow; they are
            leaving. This ends the flow and takes the tour to the next module.

            The per-step escape has not been lost: on a manual step the primary
            Next always advances and records the step as skipped, and an `auto`
            step (which has no Next) gets its own "Skip step" below.
          */}
          <button
            type="button"
            className="tour-task-callout__btn"
            onClick={() => tour?.taskSkipModule()}
            data-testid="tour-task-skip-module"
          >
            Skip module
          </button>

          {/*
            Next is deliberately absent on an auto step rather than disabled: it
            advances the instant its goal is met, so a Next button there could
            never be clicked in a state where it would do anything — an enabled-
            looking control that is unreachable by construction. The waiting line
            takes its place and says what is happening.
          */}
          {step.auto ? (
            <>
              {/*
                🔴 THE PER-STEP ESCAPE LIVES HERE AND ONLY HERE, because this is
                the only step shape with no Next. An `auto` step advances itself
                — until its action does not land (a control that never appears, a
                click the host swallows), and then the callout is a dead end.
                A step that acts on your behalf still needs a way past it when
                the action fails, and "Skip module" is too big a hammer for one
                stuck step in an otherwise-working flow.
              */}
              <button
                type="button"
                className="tour-task-callout__btn"
                onClick={() => tour?.taskSkip()}
                data-testid="tour-task-skip"
              >
                Skip step
              </button>
              <span className="tour-task-callout__waiting" data-testid="tour-task-waiting">
                {step.waitingHint ?? 'Working…'}
              </span>
            </>
          ) : (
            /*
              🔴 NEVER DISABLED. It used to be `disabled={!step.satisfied}`, which
              is what "the Next button is not working" was: a step whose predicate
              disagreed with the form — for any of the reasons in
              `TaskEngine.next` — left the only way forward greyed out and inert,
              and nothing on screen explained why. A walkthrough must not be able
              to trap the person it is walking.

              The hint below still says what the step was looking for, so pressing
              on with a field unfilled is an informed choice rather than a silent
              one, and the bar marks the step skipped rather than done.
            */
            <>
              {/*
                The way past ONE SEGMENT of a multi-part flow, landing on the next
                segment's opening step. Offered only where a step names a later
                step to jump to; a segment with nothing after it is what "Skip
                module" is for, so this no longer doubles as that.

                Rendered BEFORE the primary button so the two read
                "move on | do it", with the invitation last and emphasised.
              */}
              {step.canSkipSegment && (
                <button
                  type="button"
                  className="tour-task-callout__btn"
                  onClick={() => tour?.taskSkipSegment()}
                  data-testid="tour-task-skip-segment"
                >
                  Skip section
                </button>
              )}
              {/*
                🔴 NO "Skip step" ON A MANUAL STEP, DELIBERATELY — it was added
                here and taken out again, so do not add it back without reading
                this.

                The argument for it was that "Skip module" is the only control on
                a manual step with the word "skip" on it, and that one ends the
                whole walkthrough — so a user reaching for "skip one field" tore
                the flow down instead. The argument against it won: a manual step
                ALREADY has a per-step escape, because Next always advances and
                records the step as skipped rather than done (see
                `TaskEngine.next`), so the button was a fourth control doing what
                the primary one already did. Four buttons on a callout that sits
                beside a form field is its own problem.

                So the row here is Back | Skip module | (Skip section) | Next, and
                Next is how you pass a single step. An `auto` step has no Next,
                which is why "Skip step" still exists on that branch above and
                only there.
              */}
              <button
                type="button"
                className="tour-task-callout__btn tour-task-callout__btn--primary"
                onClick={() => tour?.taskNext()}
                data-testid="tour-task-next"
              >
                {step.nextLabel ?? 'Next'}
              </button>
            </>
          )}
        </div>

        {/* Why Next is unavailable, said out loud rather than left to a tooltip. */}
        {!step.auto && !step.satisfied && step.waitingHint && (
          <p className="tour-task-callout__hint">{step.waitingHint}</p>
        )}
      </aside>
    </>,
    document.body,
  );
}
